import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils.ts";
import { PickerSearchInput } from "./picker-search-input.tsx";
import {
  handlePickerSubmit,
  PICKER_HEADER_CLASS,
  pickerPillClass,
  scrollToItem,
  useFindScrollParent,
  usePickerKeyboard,
} from "./usePickerKeyboard.ts";

interface PresetPickerProps {
  options: string[];
  onSelect: (value: string) => void;
  defaultValue?: string | null;
  defaultIndex?: number;
  placeholder?: string;
  ariaLabel?: string;
  /** `"pills"` renders wrapped pill buttons, `"list"` renders a vertical list. */
  layout?: "pills" | "list";
  /** Optional prefix shown before each pill label (e.g. trigger word). */
  pillPrefix?: string;
}

/**
 * Generic preset-option picker with type-to-filter and free-text fallback.
 *
 * Supports two layouts:
 * - `"pills"` — wrapped pill buttons with 4-direction keyboard nav (like ContextInput)
 * - `"list"` — vertical scrollable list with up/down nav (like DurationPicker)
 */
export function PresetPicker({
  options,
  onSelect,
  defaultValue,
  defaultIndex = 0,
  placeholder = "Type to filter or enter custom...",
  ariaLabel = "Options",
  layout = "list",
  pillPrefix,
}: PresetPickerProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const scrollRef = useFindScrollParent(rootRef);
  const inputRef = useRef<HTMLInputElement>(null);

  const defaultOptionIndex = defaultValue ? options.indexOf(defaultValue) : -1;

  const [freeText, setFreeText] = useState(
    defaultOptionIndex < 0 && defaultValue ? defaultValue : "",
  );

  const filteredOptions = useMemo(() => {
    const text = freeText.trim().toLowerCase();
    if (!text) return options;
    return options.filter((opt) => opt.toLowerCase().includes(text));
  }, [options, freeText]);

  const orientation = layout === "pills" ? ("both" as const) : ("vertical" as const);

  const {
    selectedIndex,
    setSelectedIndex,
    handleKeyDown: handlePickerKeyDown,
  } = usePickerKeyboard({
    itemCount: filteredOptions.length,
    onSelect: (index) => {
      const option = filteredOptions[index];
      if (option) onSelect(option);
    },
    defaultIndex: defaultOptionIndex >= 0 ? defaultOptionIndex : defaultIndex,
    orientation,
  });

  // Scroll after index changes (list layout only)
  // biome-ignore lint/correctness/useExhaustiveDependencies: scrollRef.current is a mutable ref
  useEffect(() => {
    if (layout === "list") {
      scrollToItem(scrollRef.current, selectedIndex);
    }
  }, [selectedIndex, layout]);

  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const isArrow =
        e.key === "ArrowDown" ||
        e.key === "ArrowUp" ||
        (layout === "pills" && (e.key === "ArrowLeft" || e.key === "ArrowRight"));

      if (isArrow) {
        handlePickerKeyDown(e);
        return;
      }

      handlePickerSubmit(e, {
        filteredOptions,
        selectedIndex,
        freeText,
        onSelect,
      });
    },
    [freeText, filteredOptions, selectedIndex, onSelect, handlePickerKeyDown, layout],
  );

  // Pills layout: redirect printable chars to the text input for type-to-filter
  const handlePillsKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        inputRef.current?.focus();
        setFreeText((prev) => prev + e.key);
        return;
      }
      handlePickerKeyDown(e);
    },
    [handlePickerKeyDown],
  );

  return (
    <div
      ref={rootRef}
      tabIndex={-1}
      className="flex flex-col outline-none"
      role="listbox"
      aria-label={ariaLabel}
    >
      <div className={PICKER_HEADER_CLASS} data-sticky-header>
        <PickerSearchInput
          ref={inputRef}
          type="text"
          value={freeText}
          onChange={(e) => setFreeText(e.target.value)}
          onKeyDown={handleInputKeyDown}
          onClear={() => setFreeText("")}
          placeholder={placeholder}
          autoFocus
        />
      </div>

      {layout === "pills" && filteredOptions.length > 0 && (
        <div
          className="flex flex-wrap gap-1.5 outline-none px-3 py-2"
          tabIndex={0}
          onKeyDown={handlePillsKeyDown}
        >
          {filteredOptions.map((option, index) => (
            <button
              key={option}
              type="button"
              className={pickerPillClass(index === selectedIndex)}
              onMouseDown={(e) => {
                e.preventDefault();
                onSelect(option);
              }}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              {pillPrefix ? `${pillPrefix} ${option}` : option}
            </button>
          ))}
        </div>
      )}

      {layout === "list" &&
        filteredOptions.map((option, i) => (
          <div
            key={option}
            data-index={i}
            role="option"
            tabIndex={-1}
            aria-selected={i === selectedIndex}
            className={cn(
              "px-3 py-2 text-sm cursor-pointer",
              i === selectedIndex && "bg-secondary",
            )}
            onMouseDown={(e) => {
              e.preventDefault();
              onSelect(option);
            }}
          >
            {option}
          </div>
        ))}
    </div>
  );
}
